import React, { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import DataTable from 'react-data-table-component';
import { MdLocalOffer, MdStore, MdEvent } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { fetchManufacturerDiscounts } from '../../../../services/Http/Api';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import TableCellWithIcon from '../../../../shared/layout/tables/TableCellWithIcon';
import { formatDiscountBadge } from '../../../../utils/discountDisplay';

const ManufacturerDiscounts = () => {
    const { t } = useTranslation();
    const defaultPerPage = 10;
    const [discounts, setDiscounts] = useState([]);
    const [summary, setSummary] = useState({ active: 0, upcoming: 0, expired: 0 });
    const [meta, setMeta] = useState({ current_page: 1, per_page: defaultPerPage, total: 0, last_page: 1 });
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(true);

    const load = useCallback(async ({ page, perPage, status }) => {
        setLoading(true);
        try {
            const response = await fetchManufacturerDiscounts({
                page,
                per_page: perPage,
                status: status === 'all' ? undefined : status,
            });
            setDiscounts(response.data || []);
            setSummary(response.summary || { active: 0, upcoming: 0, expired: 0 });
            setMeta(response.meta || meta);
        } catch (error) {
            toast.error(t('ManufacturerDiscounts.error'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        load({ page: 1, perPage: defaultPerPage, status: statusFilter });
    }, [statusFilter, load]);

    const handlePageChange = (nextPage) => {
        if (nextPage < 1 || nextPage > meta.last_page) return;
        load({ page: nextPage, perPage: meta.per_page, status: statusFilter });
    };

    const columns = useMemo(() => [
        {
            name: t('ManufacturerDiscounts.table.product'),
            selector: (row) => row.product_name,
            sortable: true,
            cell: (row) => <strong>{truncate(row.product_name, 20)}</strong>,
        },
        {
            name: t('ManufacturerDiscounts.table.venue'),
            selector: (row) => row.venue_name || t('ManufacturerDiscounts.table.global'),
            sortable: true,
            cell: (row) => (
                <TableCellWithIcon>
                    <MdStore size={18} color="rgba(130, 22, 146, 0.8)" />
                    {row.venue_name || t('ManufacturerDiscounts.table.global')}
                </TableCellWithIcon>
            ),
        },
        {
            name: t('ManufacturerDiscounts.table.discount'),
            selector: (row) => row.discount_percentage ?? row.discount_value,
            sortable: true,
            cell: (row) => (
                <TableCellWithIcon>
                    <MdLocalOffer size={18} color="rgba(130, 22, 146, 0.8)" />
                    <DiscountBadge>-{formatDiscountBadge(row)}</DiscountBadge>
                </TableCellWithIcon>
            ),
            right: true,
        },
        {
            name: t('ManufacturerDiscounts.table.end'),
            selector: (row) => new Date(row.end_at),
            sortable: true,
            cell: (row) => (
                <TableCellWithIcon>
                    <MdEvent size={18} color="rgba(130, 22, 146, 0.8)" />
                    {new Intl.DateTimeFormat().format(new Date(row.end_at))}
                </TableCellWithIcon>
            ),
        },
        {
            name: t('ManufacturerDiscounts.table.status'),
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => (
                <StatusBadge $status={row.status}>
                    {t(`ManufacturerDiscounts.status.${row.status}`)}
                </StatusBadge>
            ),
        },
    ], [t]);

    return (
        <PageContainer>
            <PageHeader>
                <div>
                    <PageTitle>{t('ManufacturerDiscounts.title')}</PageTitle>
                    <Subtitle>{t('ManufacturerDiscounts.subtitle')}</Subtitle>
                </div>
                <ImportButton onClick={() => toast.info(t('ManufacturerDiscounts.importSoon'))}>
                    {t('ManufacturerDiscounts.importButton')}
                </ImportButton>
            </PageHeader>

            <PageContent>
                <SummaryGrid>
                    <SummaryCard>
                        <SummaryLabel>{t('ManufacturerDiscounts.summary.active')}</SummaryLabel>
                        <SummaryValue>{summary.active || 0}</SummaryValue>
                    </SummaryCard>
                    <SummaryCard>
                        <SummaryLabel>{t('ManufacturerDiscounts.summary.upcoming')}</SummaryLabel>
                        <SummaryValue>{summary.upcoming || 0}</SummaryValue>
                    </SummaryCard>
                    <SummaryCard>
                        <SummaryLabel>{t('ManufacturerDiscounts.summary.expired')}</SummaryLabel>
                        <SummaryValue>{summary.expired || 0}</SummaryValue>
                    </SummaryCard>
                </SummaryGrid>

                <StatusFilters>
                    {['all', 'active', 'upcoming', 'expired'].map((status) => (
                        <FilterButton
                            key={status}
                            $active={statusFilter === status}
                            onClick={() => setStatusFilter(status)}
                        >
                            {status === 'all' ? t('ManufacturerDiscounts.filters.all') : t(`ManufacturerDiscounts.status.${status}`)}
                        </FilterButton>
                    ))}
                </StatusFilters>

                {loading ? (
                    <Placeholder>{t('common.loading')}</Placeholder>
                ) : discounts.length === 0 ? (
                    <Placeholder>{t('ManufacturerDiscounts.empty')}</Placeholder>
                ) : (
                    <DataTable
                        columns={columns}
                        data={discounts}
                        pagination
                        paginationServer
                        paginationTotalRows={meta.total}
                        paginationPerPage={meta.per_page}
                        onChangePage={handlePageChange}
                        progressPending={loading}
                        highlightOnHover
                        noDataComponent={<Placeholder>{t('ManufacturerDiscounts.empty')}</Placeholder>}
                    />
                )}
            </PageContent>
        </PageContainer>
    );
};

export default ManufacturerDiscounts;

const Subtitle = styled.p`
    margin: 0;
    color: #555;
    flex: 1;
`;

const ImportButton = styled.button`
    background: rgba(130, 22, 146, 1);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 0.7rem 1.5rem;
    cursor: pointer;
    font-weight: 600;

    &:hover {
        opacity: 0.9;
    }
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
`;

const SummaryCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1rem;
    background: #fff;
    box-shadow: 0 4px 20px rgba(130, 22, 146, 0.08);
`;

const SummaryLabel = styled.p`
    margin: 0;
    color: #777;
`;

const SummaryValue = styled.p`
    margin: 0;
    font-size: 2rem;
    color: rgba(130, 22, 146, 1);
    font-weight: 700;
`;

const StatusFilters = styled.div`
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
`;

const FilterButton = styled.button`
    padding: 0.4rem 1rem;
    border-radius: 999px;
    border: 1px solid rgba(130, 22, 146, 0.3);
    background: ${({ $active }) => ($active ? 'rgba(130, 22, 146, 0.1)' : '#fff')};
    color: ${({ $active }) => ($active ? 'rgba(130, 22, 146, 1)' : '#555')};
    cursor: pointer;
    font-weight: 600;
    transition: background 0.2s ease;

    &:hover {
        background: rgba(130, 22, 146, 0.12);
    }
`;

const Placeholder = styled.div`
    padding: 2.5rem;
    text-align: center;
    border: 1px dashed rgba(130, 22, 146, 0.3);
    border-radius: 12px;
    color: #777;
`;

const DiscountBadge = styled.span`
    padding: 0.2rem 0.6rem;
    border-radius: 6px;
    background: rgba(130, 22, 146, 0.1);
    color: rgba(130, 22, 146, 1);
    font-weight: 600;
`;

const StatusBadge = styled.span`
    padding: 0.3rem 0.8rem;
    border-radius: 999px;
    font-size: 0.875rem;
    text-transform: capitalize;
    background: ${({ $status }) => {
        switch ($status) {
            case 'active':
                return 'rgba(76, 175, 80, 0.15)';
            case 'upcoming':
                return 'rgba(255, 193, 7, 0.15)';
            default:
                return 'rgba(158, 158, 158, 0.15)';
        }
    }};
    color: ${({ $status }) => {
        switch ($status) {
            case 'active':
                return '#2e7d32';
            case 'upcoming':
                return '#f57c00';
            default:
                return '#616161';
        }
    }};
`;

const truncate = (value, maxLength = 80) => {
    if (!value) return '';
    return value.length > maxLength ? `${value.slice(0, maxLength)}…` : value;
};
